import { useCallback, useEffect, useRef } from "react";

export type Options = {
  /**
   * Sets the key press handler to on or off.
   */
  isActive?: boolean;
};

const defaultOptions: Options = { isActive: true };

/**
 * useOnKeyPress detects key down events for a given key.
 * @param targetKey Key to listen for.
 * @param handler Function to call when user presses the given key.
 * @param options
 */
export const useOnKeyPress = <T extends HTMLElement>(
  targetKey: string,
  handler?: (...args: any[]) => any,
  options: Options = defaultOptions
) => {
  const { isActive = true } = options;

  const downHandler = useCallback(
    (event: KeyboardEvent) => {
      if (isActive && event.key === targetKey) {
        handler?.(event);
      }
    },
    [targetKey, handler, isActive]
  );

  const ref = useRef<Window | Element | null>(window);
  const refCallback = useCallback(
    (node: T) => {
      if (ref.current) {
        ref.current.removeEventListener(
          "keydown",
          downHandler as EventListener
        );
      }
      if (node) {
        node.addEventListener("keydown", downHandler as EventListener);
      }
      ref.current = node;
    },
    [downHandler]
  );

  useEffect(() => {
    if (ref.current !== window) {
      return;
    }
    window.addEventListener("keydown", downHandler as EventListener);
    return () => {
      window.removeEventListener("keydown", downHandler as EventListener);
    };
  }, [downHandler]);

  return [refCallback];
};
